import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  Card,
  Button,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Stack,
  Avatar,
  Pagination,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CallIcon from '@mui/icons-material/Call';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import dayjs from 'dayjs';

import FormDialog from '../components/forms/FormDialog';
import AddAppointment from '../components/forms/AddAppointment';
import {
  fetchTokenAppointmentRequest,
  SelectedPatient,
} from 'src/modules/app/store/appActions';
import storage from 'src/utils/storageUtils';

const AppointmentContainer = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [tokenAppointments, setTokenAppointments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [openAddModal, setOpenAddModal] = useState(false);
  const [page, setPage] = useState(1);
  const perPage = 15;
  const [totalPages, setTotalPages] = useState(1);

  // Menu action
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [activeItem, setActiveItem] = useState(null);

  const selectedClinicStr = localStorage.getItem('selectedClinic');
  let selectedClinic = null;
  try {
    selectedClinic = selectedClinicStr ? JSON.parse(selectedClinicStr) : null;
  } catch (e) {
    selectedClinic = null;
  }

  const staffEmail = (localStorage.getItem('staffEmail') || localStorage.getItem('STAFFEMAIL') || '').toLowerCase();
  const isSanginiOnly = staffEmail.includes('sangini') || staffEmail === 'tapan@sanginihospital.com';

  const fetchData = async () => {
    setLoading(true);
    const token = storage.get('TOKEN');
    try {
      const response = await new Promise((resolve, reject) => {
        dispatch(fetchTokenAppointmentRequest(token, page, perPage, resolve, reject));
      });
      if (response && response.data) {
        setTokenAppointments(response.data);
        if (response.meta?.totalPages) {
          setTotalPages(response.meta.totalPages);
        }
      }
    } catch (err) {
      console.error('Failed to fetch appointments:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dispatch, page, perPage]);

  const handleViewProfile = (item) => {
    if (!item) return;
    const patientId = item.patient?.id || item.patient?._id || item.patient;
    const patientName = item.patient?.fullName || item.patient?.name || 'Patient';

    if (patientId) {
      storage.set('PATIENT_ID', patientId);
    }
    if (patientName) {
      storage.set('PATIENT_NAME', patientName);
    }
    if (item.id || item.appointmentId) {
      storage.set('APPOINTMENT_ID', item.id || item.appointmentId);
    }

    dispatch(
      SelectedPatient({
        id: patientId,
        patientId,
        patientName,
        appointmentId: item.id || item.appointmentId,
        patient: item.patient,
      })
    );

    if (menuAnchor) handleMenuClose();
    navigate('/viewprofile');
  };

  const handleCallPatient = (item) => {
    if (!item) return;
    const rawPhone = item.patient?.contactNo || item.patient?.phone || '';
    if (rawPhone) {
      window.location.href = `tel:${rawPhone}`;
    } else {
      enqueueSnackbar('Patient phone number not available', { variant: 'warning' });
    }
    if (menuAnchor) handleMenuClose();
  };

  const handleMenuOpen = (event, item) => {
    setMenuAnchor(event.currentTarget);
    setActiveItem(item);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setActiveItem(null);
  };

  const filteredAppointments = useMemo(() => {
    return tokenAppointments.filter((item) => {
      const pName = item.patient?.fullName || item.patient?.name || '';
      const pPhone = item.patient?.contactNo || '';
      const apptId = item.appointmentId || item.id || '';
      const matches =
        pName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        pPhone.includes(searchQuery) ||
        apptId.toLowerCase().includes(searchQuery.toLowerCase());

      if (!matches) return false;
      if (statusFilter === 'ALL') return true;
      if (statusFilter === 'SCHEDULED')
        return item.recordStatus === 'Scheduled' || item.recordStatus === 'In Queue';
      if (statusFilter === 'ATTENDING') return item.recordStatus === 'Attending Now';
      if (statusFilter === 'COMPLETE') return item.recordStatus === 'Complete';
      if (statusFilter === 'OVERDUE')
        return item.recordStatus === 'Expired' || item.recordStatus === 'Overdue';
      if (statusFilter === 'CANCELLED')
        return item.recordStatus === 'Cancelled';
      return true;
    });
  }, [tokenAppointments, searchQuery, statusFilter]);

  const isDark = theme.palette.mode === 'dark';

  const getStatusColor = (status) => {
    switch (status) {
      case 'Attending Now':
        return {
          bg: isDark ? 'rgba(34, 197, 94, 0.2)' : '#DCFCE7',
          text: isDark ? '#4ADE80' : '#15803D',
        };
      case 'In Queue':
      case 'Scheduled':
        return {
          bg: isDark ? 'rgba(245, 158, 11, 0.2)' : '#FEF3C7',
          text: isDark ? '#FBBF24' : '#B45309',
        };
      case 'Complete':
        return {
          bg: isDark ? 'rgba(14, 165, 233, 0.2)' : '#E0F2FE',
          text: isDark ? '#38BDF8' : '#0369A1',
        };
      case 'Expired':
      case 'Overdue':
        return {
          bg: isDark ? 'rgba(239, 68, 68, 0.2)' : '#FEE2E2',
          text: isDark ? '#F87171' : '#DC2626',
        };
      case 'Cancelled':
        return {
          bg: isDark ? 'rgba(255, 255, 255, 0.05)' : '#F1F5F9',
          text: isDark ? '#94A3B8' : '#64748B',
        };
      default:
        return {
          bg: isDark ? 'rgba(255, 255, 255, 0.05)' : '#F1F5F9',
          text: isDark ? '#94A3B8' : '#475569',
        };
    }
  };

  return (
    <Box sx={{ width: '100%', py: 1 }}>
      {/* Top Banner */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
            Appointments Schedule
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
            {selectedClinic ? selectedClinic.clinicName : 'All Clinics'} • Complete Patient Booking Registry
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5}>
          <Tooltip title="Refresh">
            <IconButton
              onClick={fetchData}
              disabled={loading}
              sx={{
                bgcolor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '10px',
                '&:hover': { bgcolor: theme.palette.action.hover },
              }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </Tooltip>

          {!isSanginiOnly && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenAddModal(true)}
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 600,
                px: 2.5,
                py: 1,
                bgcolor: theme.palette.primary.main,
                boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25)',
                '&:hover': { bgcolor: theme.palette.primary.dark },
              }}
            >
              Add Appointment
            </Button>
          )}
        </Stack>
      </Box>

      {/* Main Table Card */}
      <Card
        sx={{
          borderRadius: '16px',
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.3)' : '0 4px 16px rgba(0,0,0,0.03)',
          bgcolor: theme.palette.background.paper,
          overflow: 'hidden',
        }}
      >
        {/* Controls Bar */}
        <Box
          sx={{
            p: 2.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Tabs
            value={statusFilter}
            onChange={(e, val) => setStatusFilter(val)}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '14px',
                minHeight: '40px',
                color: theme.palette.text.secondary,
                '&.Mui-selected': {
                  color: theme.palette.primary.main,
                },
              },
            }}
          >
            <Tab label="All Appointments" value="ALL" />
            <Tab label="Scheduled / In Queue" value="SCHEDULED" />
            <Tab label="Attending Now" value="ATTENDING" />
            <Tab label="Completed" value="COMPLETE" />
            <Tab label="Overdue" value="OVERDUE" />
            <Tab label="Cancelled" value="CANCELLED" />
          </Tabs>

          <TextField
            size="small"
            placeholder="Search patient, token, phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: '100%', sm: 280 },
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                bgcolor: isDark ? 'rgba(255,255,255,0.04)' : '#FFFFFF',
              },
            }}
          />
        </Box>

        {/* Data Table */}
        <TableContainer component={Paper} elevation={0} sx={{ bgcolor: 'transparent' }}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#F8FAFC' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: theme.palette.text.secondary, fontSize: '13px', borderColor: theme.palette.divider }}>PATIENT</TableCell>
                <TableCell sx={{ fontWeight: 700, color: theme.palette.text.secondary, fontSize: '13px', borderColor: theme.palette.divider }}>DATE & TIME</TableCell>
                <TableCell sx={{ fontWeight: 700, color: theme.palette.text.secondary, fontSize: '13px', borderColor: theme.palette.divider }}>SYMPTOMS</TableCell>
                <TableCell sx={{ fontWeight: 700, color: theme.palette.text.secondary, fontSize: '13px', borderColor: theme.palette.divider }}>STATUS</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: theme.palette.text.secondary, fontSize: '13px', borderColor: theme.palette.divider }}>
                  ACTIONS
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAppointments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 8, borderColor: theme.palette.divider }}>
                    <CalendarMonthIcon sx={{ fontSize: 48, color: theme.palette.divider, mb: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                      No Appointments Found
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
                      Click "Add Appointment" to schedule a patient consultation.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredAppointments.map((row) => {
                  const patient = row.patient || {};
                  const patientName = patient.fullName || patient.name || 'Walk-in Patient';
                  
                  // Age & Gender subtitle e.g. "23 • male"
                  const subtitleParts = [];
                  let age = patient.age;
                  if (!age && (patient.dateOfBirth || patient.dob)) {
                    const diff = dayjs().diff(dayjs(patient.dateOfBirth || patient.dob), 'year');
                    if (!isNaN(diff) && diff >= 0) age = diff;
                  }
                  if (age) subtitleParts.push(`${age}`);
                  if (patient.gender) subtitleParts.push(patient.gender.toLowerCase());
                  const patientSubtitle = subtitleParts.join(' • ');

                  const statusStyle = getStatusColor(row.recordStatus);
                  const dateFormatted = row.date ? dayjs(row.date).format('DD MMM, YYYY') : '';
                  const timeFormatted = row.time || '';
                  const dateTimeDisplay = dateFormatted && timeFormatted
                    ? `${dateFormatted} ${timeFormatted}`
                    : (dateFormatted || timeFormatted || 'Today');

                  return (
                    <TableRow
                      key={row.id || row._id}
                      hover
                      sx={{
                        borderColor: theme.palette.divider,
                        '&:last-child td, &:last-child th': { border: 0 },
                        transition: 'background-color 0.2s',
                        '&:hover': {
                          bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                        },
                      }}
                    >
                      {/* Patient Name + Age • Gender */}
                      <TableCell sx={{ borderColor: theme.palette.divider }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar
                            sx={{
                              width: 38,
                              height: 38,
                              bgcolor: theme.palette.primary.main,
                              color: '#FFFFFF',
                              fontSize: '14px',
                              fontWeight: 700,
                            }}
                          >
                            {patientName.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                              {patientName}
                            </Typography>
                            {patientSubtitle && (
                              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                                {patientSubtitle}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Date & Time */}
                      <TableCell sx={{ borderColor: theme.palette.divider }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                          {dateTimeDisplay}
                        </Typography>
                      </TableCell>

                      {/* Symptoms */}
                      <TableCell sx={{ borderColor: theme.palette.divider }}>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                          {row.addnotes || row.symptoms || 'fever'}
                        </Typography>
                      </TableCell>

                      {/* Status */}
                      <TableCell sx={{ borderColor: theme.palette.divider }}>
                        <Chip
                          label={row.recordStatus || 'Scheduled'}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            fontSize: '12px',
                            bgcolor: statusStyle.bg,
                            color: statusStyle.text,
                            px: 1,
                            py: 0.5,
                            borderRadius: '16px',
                          }}
                        />
                      </TableCell>

                      {/* Actions: Call & 3-Dots Menu */}
                      <TableCell align="right" sx={{ borderColor: theme.palette.divider }}>
                        <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                          <Tooltip title="Call Patient">
                            <IconButton
                              size="small"
                              onClick={() => handleCallPatient(row)}
                              sx={{
                                border: `1px solid ${isDark ? '#334155' : '#E0E7FF'}`,
                                bgcolor: isDark ? 'rgba(79, 70, 229, 0.15)' : '#EEF2FF',
                                color: isDark ? '#A5B4FC' : '#4F46E5',
                                '&:hover': { bgcolor: isDark ? 'rgba(79, 70, 229, 0.25)' : '#E0E7FF' },
                              }}
                            >
                              <CallIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, row)}
                            sx={{
                              bgcolor: theme.palette.primary.main,
                              color: '#FFFFFF',
                              '&:hover': { bgcolor: theme.palette.primary.dark },
                              width: 32,
                              height: 32,
                            }}
                          >
                            <MoreVertIcon fontSize="small" sx={{ color: '#FFFFFF' }} />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination Bar */}
        {totalPages > 1 && (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', borderTop: `1px solid ${theme.palette.divider}` }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, val) => setPage(val)}
              color="primary"
              shape="rounded"
            />
          </Box>
        )}
      </Card>

      {/* Row Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: '10px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            minWidth: 160,
          },
        }}
      >
        <MenuItem
          onClick={() => handleViewProfile(activeItem)}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}
        >
          <PersonOutlineIcon fontSize="small" sx={{ color: '#4F46E5' }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            View Profile
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={() => handleCallPatient(activeItem)}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}
        >
          <CallIcon fontSize="small" sx={{ color: '#059669' }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Call
          </Typography>
        </MenuItem>
      </Menu>

      {/* Add Appointment Modal */}
      <FormDialog open={openAddModal} onClose={() => setOpenAddModal(false)}>
        <AddAppointment
          onClose={() => setOpenAddModal(false)}
          onSave={() => {
            fetchData();
            setOpenAddModal(false);
          }}
          onPatientAdded={fetchData}
        />
      </FormDialog>
    </Box>
  );
};

export default AppointmentContainer;
